/**
 * Camera Controller
 * Handles dynamic camera movement to follow and zoom on dice
 *
 * Behavior:
 * - Default: Returns to default position (0, 14, 10) looking at origin
 * - Rolling: Follows dice to keep all in view
 * - Stopped: Zooms in as close as possible while keeping all dice visible
 * - Betting: GSAP tween to betting anchor position
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { Dice } from '../dice/Dice';
import { CameraAnims } from '../config/AnimationConfig';

// Reusable vectors to avoid per-frame allocations
const _boundsCenter = new THREE.Vector3();
const _viewDir = new THREE.Vector3();
const _tempPosition = new THREE.Vector3();

export interface CameraControllerConfig {
  defaultPosition?: { x: number; y: number; z: number };
  defaultLookAt?: { x: number; y: number; z: number };
  bettingPosition?: { x: number; y: number; z: number };
  bettingLookAt?: { x: number; y: number; z: number };
  lerpSpeed?: number;
  zoomLerpSpeed?: number;
  minHeight?: number;
  padding?: number;
  diceSize?: number;
}

const DEFAULT_CONFIG: Required<CameraControllerConfig> = {
  defaultPosition: { x: 0, y: 14, z: 10 },
  defaultLookAt: { x: 0, y: 0, z: 0 },
  bettingPosition: { x: 3.2, y: 10.2, z: 7.8 },
  bettingLookAt: { x: 0.8, y: 1.1, z: 1.6 },
  lerpSpeed: CameraAnims.followLerpSpeed,
  zoomLerpSpeed: CameraAnims.zoomLerpSpeed,
  minHeight: 6,
  padding: 2.5,
  diceSize: 1,
};

export type CameraMode = 'default' | 'follow' | 'zoom' | 'zoom_unheld' | 'betting';

interface DiceBounds {
  center: THREE.Vector3;
  width: number;
  depth: number;
}

export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private config: Required<CameraControllerConfig>;
  private dice: Dice[] = [];

  // Camera targets
  private defaultPosition: THREE.Vector3;
  private defaultLookAt: THREE.Vector3;
  private bettingPosition: THREE.Vector3;
  private bettingLookAt: THREE.Vector3;
  private bettingOffset: THREE.Vector3;
  private targetPosition: THREE.Vector3;
  private targetLookAt: THREE.Vector3;
  private currentLookAt: THREE.Vector3;

  // Current mode
  private mode: CameraMode = 'default';

  // GSAP transition tween (for default ↔ betting transitions)
  private transitionTween: gsap.core.Timeline | null = null;

  // Resolvers waiting on the current transitionTween to settle. Anyone
  // calling enterBetting/exitBetting registers here; the tween's
  // onComplete fires all of them and clears the list. Survives kill+
  // restart (resolvers stay queued and fire on whichever tween eventually
  // completes naturally), so a sibling like tweenToBettingTarget that
  // replaces the in-flight tween mid-flight doesn't orphan awaiters.
  private tweenSettleResolvers: Array<() => void> = [];

  // Enabled state (for free cam toggle)
  private enabled = true;

  constructor(camera: THREE.PerspectiveCamera, config?: CameraControllerConfig) {
    this.camera = camera;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Initialize vectors
    this.defaultPosition = new THREE.Vector3(
      this.config.defaultPosition.x,
      this.config.defaultPosition.y,
      this.config.defaultPosition.z
    );
    this.defaultLookAt = new THREE.Vector3(
      this.config.defaultLookAt.x,
      this.config.defaultLookAt.y,
      this.config.defaultLookAt.z
    );
    this.bettingPosition = new THREE.Vector3(
      this.config.bettingPosition.x,
      this.config.bettingPosition.y,
      this.config.bettingPosition.z
    );
    this.bettingLookAt = new THREE.Vector3(
      this.config.bettingLookAt.x,
      this.config.bettingLookAt.y,
      this.config.bettingLookAt.z
    );
    this.bettingOffset = this.bettingPosition.clone().sub(this.bettingLookAt);

    this.targetPosition = this.defaultPosition.clone();
    this.targetLookAt = this.defaultLookAt.clone();
    this.currentLookAt = this.defaultLookAt.clone();
  }

  /**
   * Set the dice to track
   */
  setDice(dice: Dice[]): void {
    this.dice = dice;
  }

  /**
   * Set camera mode. Triggers GSAP tween for default/betting transitions.
   * Follow/zoom modes use per-frame lerp (dice targets move every frame).
   */
  setMode(mode: CameraMode, onComplete?: () => void): void {
    const changed = mode !== this.mode;
    this.mode = mode;
    if (!changed) { onComplete?.(); return; }

    // Kill any running transition
    if (this.transitionTween) {
      this.transitionTween.kill();
      this.transitionTween = null;
    }

    if (mode === 'betting') {
      this.tweenTo(this.bettingPosition, this.bettingLookAt, CameraAnims.toBettingDuration, CameraAnims.toBettingEase, onComplete);
    } else if (mode === 'default') {
      this.tweenTo(this.defaultPosition, this.defaultLookAt, CameraAnims.toDefaultDuration, CameraAnims.toDefaultEase, onComplete);
    } else {
      // follow/zoom: per-frame lerp, no tween — fire immediately
      onComplete?.();
    }
  }

  /**
   * Start a GSAP tween from current camera state to target position + lookAt.
   */
  private tweenTo(
    targetPos: THREE.Vector3,
    targetLookAt: THREE.Vector3,
    duration: number,
    ease: string,
    onComplete?: () => void
  ): void {
    this.transitionTween = gsap.timeline({
      onComplete: () => {
        this.transitionTween = null;
        // Drain queued enterBetting/exitBetting awaiters. They were waiting
        // on whichever tween ended up settling — could be this one, could
        // be one a sibling (tweenToBettingTarget) replaced us with. Either
        // way, the camera is now at rest at its target.
        const resolvers = this.tweenSettleResolvers;
        this.tweenSettleResolvers = [];
        for (const r of resolvers) r();
        onComplete?.();
      },
    })
      .to(this.camera.position, {
        x: targetPos.x, y: targetPos.y, z: targetPos.z,
        duration, ease,
      }, 0)
      .to(this.currentLookAt, {
        x: targetLookAt.x, y: targetLookAt.y, z: targetLookAt.z,
        duration, ease,
      }, 0);
  }

  setBettingBounds(bounds: { minX: number; maxX: number; minZ: number; maxZ: number }): void {
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;
    const extent = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ);

    const baseDistance = this.bettingOffset.length();
    const extraDistance = Math.max(0, extent - CameraAnims.boundsZoomOutThreshold) * CameraAnims.boundsZoomOutScaling;
    const offsetDir = this.bettingOffset.clone().normalize();
    const newOffset = offsetDir.multiplyScalar(baseDistance + extraDistance);

    this.bettingLookAt.set(centerX, this.bettingLookAt.y, centerZ);
    this.bettingPosition.copy(this.bettingLookAt).add(newOffset);
  }

  setBettingBoundsLockedCenter(
    bounds: { minX: number; maxX: number; minZ: number; maxZ: number },
    center: { x: number; z: number }
  ): void {
    const extent = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ);
    const baseDistance = this.bettingOffset.length();
    const extraDistance = Math.max(0, extent - CameraAnims.boundsZoomOutThreshold) * CameraAnims.boundsZoomOutScaling;
    const offsetDir = this.bettingOffset.clone().normalize();
    const newOffset = offsetDir.multiplyScalar(baseDistance + extraDistance);

    this.bettingLookAt.set(center.x, this.bettingLookAt.y, center.z);
    this.bettingPosition.copy(this.bettingLookAt).add(newOffset);
  }

  /**
   * Set betting camera from anchor data (position + lookAt + optional FOV).
   */
  setBettingAnchor(
    position: { x: number; y: number; z: number },
    lookAt: { x: number; y: number; z: number },
    fov?: number
  ): void {
    this.bettingPosition.set(position.x, position.y, position.z);
    this.bettingLookAt.set(lookAt.x, lookAt.y, lookAt.z);
    this.bettingOffset = this.bettingPosition.clone().sub(this.bettingLookAt);
    if (fov != null) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * Zoom out just enough to fit bounds while keeping current lookAt.
   * Uses FOV-based calculation to guarantee all zones are visible.
   * Half-widths are measured from the lookAt point (not bounds center)
   * so asymmetric zone placement doesn't cause clipping.
   */
  setBettingBoundsKeepLookAt(
    bounds: { minX: number; maxX: number; minZ: number; maxZ: number },
    opts: { maxExtraDolly?: number } = {},
  ): void {
    // Compute camera distance so the entire bounds rectangle fits in the
    // frustum, with the lookAt held at its current value (editor anchor +
    // sidebet nudge). LookAt is preserved (the "KeepLookAt" half of the
    // name) so the editor's intended composition stays intact between
    // updates.
    //
    // BOUNDS_PADDING expands the bounds by a fixed world-unit margin
    // before the FOV math runs — gives bills overflowing the zone radius
    // some breathing room and softens the FOV math's under-count of the
    // angled ground-plane extent. Kept conservative (0.3) so the camera
    // doesn't over-dolly when the bounds are tight: for ring-1 / ring-2
    // wings the natural extent already does the work.
    const BOUNDS_PADDING = 0.3;

    const halfX = Math.max(
      Math.abs(bounds.maxX - this.bettingLookAt.x),
      Math.abs(bounds.minX - this.bettingLookAt.x),
    ) + BOUNDS_PADDING;
    const halfZ = Math.max(
      Math.abs(bounds.maxZ - this.bettingLookAt.z),
      Math.abs(bounds.minZ - this.bettingLookAt.z),
    ) + BOUNDS_PADDING;

    const vFov = this.camera.fov * (Math.PI / 180);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);

    const dForX = halfX / Math.tan(hFov / 2);
    const dForZ = halfZ / Math.tan(vFov / 2);
    const requiredDistance = Math.max(dForX, dForZ);

    const baseDistance = this.bettingOffset.length();
    // Clamp the extra dolly so a stray large bounds rectangle can't run the
    // camera way back. The 5-sidebet cap means the geometry is bounded; this
    // keeps the dolly a slight adjustment to the editor anchor.
    const maxExtra = opts.maxExtraDolly ?? Infinity;
    const extra = Math.min(Math.max(0, requiredDistance - baseDistance), maxExtra);
    const finalDistance = baseDistance + extra;

    const offsetDir = this.bettingOffset.clone().normalize();
    this.bettingPosition.copy(this.bettingLookAt).add(offsetDir.multiplyScalar(finalDistance));
  }

  /**
   * Smoothly tween camera to the current bettingPosition/bettingLookAt.
   * Use after updating bounds to animate the zoom-out.
   */
  tweenToBettingTarget(duration = CameraAnims.bettingBoundsTweenDuration): void {
    if (this.mode !== 'betting') return;
    // Kill any running transition so we don't fight it
    if (this.transitionTween) {
      this.transitionTween.kill();
      this.transitionTween = null;
    }
    this.tweenTo(this.bettingPosition, this.bettingLookAt, duration, CameraAnims.bettingBoundsTweenEase);
  }

  setBettingFocus(position: { x: number; z: number }): void {
    this.bettingLookAt.set(position.x, this.bettingLookAt.y, position.z);
    this.bettingPosition.copy(this.bettingLookAt).add(this.bettingOffset);
  }

  isBettingSettled(positionEpsilon = 0.02, lookAtEpsilon = 0.02): boolean {
    const posDelta = this.camera.position.distanceToSquared(this.bettingPosition);
    const lookDelta = this.currentLookAt.distanceToSquared(this.bettingLookAt);
    return posDelta <= positionEpsilon * positionEpsilon && lookDelta <= lookAtEpsilon * lookAtEpsilon;
  }

  // ── Async target-mode API ────────────────────────────────────────────
  // Promise-based intent declarations for the SDL state machine. State
  // classes call enterBetting / exitBetting in onEnter and await each one
  // in sequence — the same shape as udgpoker's component methods.

  /**
   * Move (or snap) the camera to the betting anchor. Idempotent: if the
   * camera is already at the betting anchor with no lerp in flight,
   * resolves immediately. Otherwise lerps and resolves when the lerp
   * finishes.
   *
   * Single-owner contract: while this promise is pending, no other code
   * path is expected to start or kill the camera tween. The bounds-based
   * zoom-out (tweenToBettingTarget) is gated on isBettingSettled() so it
   * can't fire during the initial entry.
   */
  enterBetting(opts: { animated?: boolean } = {}): Promise<void> {
    return this.transitionToMode('betting', opts.animated ?? true);
  }

  /**
   * Move (or snap) the camera back to the default (shooting) anchor.
   * Same idempotency contract as enterBetting.
   */
  exitBetting(opts: { animated?: boolean } = {}): Promise<void> {
    return this.transitionToMode('default', opts.animated ?? true);
  }

  private transitionToMode(target: 'betting' | 'default', animated: boolean): Promise<void> {
    // Idempotent: same mode, no lerp in flight → no-op.
    if (this.mode === target && !this.transitionTween) {
      return Promise.resolve();
    }

    if (!animated) {
      this.snapToMode(target);
      return Promise.resolve();
    }

    // Different target → kick off a new tween via setMode. Same target +
    // tween in flight → don't restart (setMode would no-op + synchronously
    // fire its onComplete, releasing our awaiter while the camera is still
    // mid-lerp). Either way, register a resolver on the current
    // transitionTween's settle queue and let its onComplete fire it.
    if (this.mode !== target) {
      this.setMode(target);
    }
    if (!this.transitionTween) {
      // setMode early-returned without starting a tween (target was
      // 'follow'/'zoom' or some other no-tween mode). Resolve immediately.
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.tweenSettleResolvers.push(resolve);
    });
  }

  /** Instantly teleport the camera to a target mode's pose. Used by the
   *  animated:false path (late-join / state:full restore) and on phase
   *  exit when an in-flight lerp must be force-completed. */
  private snapToMode(target: 'betting' | 'default'): void {
    if (this.transitionTween) {
      this.transitionTween.kill();
      this.transitionTween = null;
    }
    this.mode = target;
    const pos = target === 'betting' ? this.bettingPosition : this.defaultPosition;
    const look = target === 'betting' ? this.bettingLookAt : this.defaultLookAt;
    this.camera.position.copy(pos);
    this.currentLookAt.copy(look);
    this.targetPosition.copy(pos);
    this.targetLookAt.copy(look);
    this.camera.lookAt(this.currentLookAt);
    // We just snapped to target — drain any awaiters expecting "tween
    // settled". They're effectively settled (instantaneously).
    const resolvers = this.tweenSettleResolvers;
    this.tweenSettleResolvers = [];
    for (const r of resolvers) r();
  }

  /**
   * Get current mode
   */
  getMode(): CameraMode {
    return this.mode;
  }

  /**
   * Enable or disable camera controller (for free cam mode)
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if camera controller is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Update camera (call every frame)
   */
  update(deltaTime: number): void {
    // Skip update if disabled (free cam mode)
    if (!this.enabled) return;

    // GSAP transition active — just update lookAt each frame
    if (this.transitionTween) {
      this.camera.lookAt(this.currentLookAt);
      return;
    }

    // No dice and not in betting → drift to default via per-frame lerp
    if (this.dice.length === 0 && this.mode !== 'betting') {
      this.returnToDefault(deltaTime);
      return;
    }

    switch (this.mode) {
      case 'follow':
        this.updateFollowCamera(deltaTime, false, false);
        break;
      case 'zoom':
        this.updateFollowCamera(deltaTime, true, false);
        break;
      case 'zoom_unheld':
        this.updateFollowCamera(deltaTime, true, true);
        break;
      case 'betting':
        // Tween complete, camera is at target — maintain lookAt
        this.camera.lookAt(this.currentLookAt);
        break;
      case 'default':
      default:
        // Tween complete or steady state — maintain lookAt
        this.camera.lookAt(this.currentLookAt);
        break;
    }
  }

  /**
   * Return camera to default position via GSAP tween.
   * Fires once — subsequent frames are handled by transitionTween path in update().
   */
  private returnToDefault(_deltaTime: number): void {
    // Already at default — just maintain lookAt
    if (this.camera.position.distanceToSquared(this.defaultPosition) < 0.0001
        && this.currentLookAt.distanceToSquared(this.defaultLookAt) < 0.0001) {
      this.camera.lookAt(this.currentLookAt);
      return;
    }

    // Start GSAP tween if not already tweening
    if (!this.transitionTween) {
      this.tweenTo(this.defaultPosition, this.defaultLookAt, CameraAnims.returnToDefaultDuration, CameraAnims.returnToDefaultEase);
    }
  }

  /**
   * Update camera to follow/zoom on dice (per-frame lerp — target moves every frame)
   */
  private updateFollowCamera(deltaTime: number, zoom: boolean, unheldOnly: boolean): void {
    const bounds = this.getDiceBounds(unheldOnly);

    // If no valid bounds (all dice held), return to default
    if (!bounds) {
      this.returnToDefault(deltaTime);
      return;
    }

    const target = this.calculateCameraForBounds(bounds, zoom);

    this.targetPosition.copy(target.position);
    this.targetLookAt.copy(target.lookAt);

    let lerpSpeed = zoom ? this.config.zoomLerpSpeed : this.config.lerpSpeed;
    if (zoom) {
      for (const die of this.dice) {
        if (die.held) {
          lerpSpeed *= CameraAnims.heldDiceSpeedMultiplier;
          break;
        }
      }
    }
    const lerpFactor = lerpSpeed * deltaTime;

    this.camera.position.lerp(this.targetPosition, lerpFactor);
    this.currentLookAt.lerp(this.targetLookAt, lerpFactor);
    if (this.camera.position.distanceTo(this.targetPosition) < 0.01) {
      this.camera.position.copy(this.targetPosition);
    }
    if (this.currentLookAt.distanceTo(this.targetLookAt) < 0.01) {
      this.currentLookAt.copy(this.targetLookAt);
    }
    this.camera.lookAt(this.currentLookAt);
  }

  /**
   * Calculate bounding box of dice (optionally only unheld dice)
   * Uses reusable vectors to avoid per-frame allocations
   */
  private getDiceBounds(unheldOnly = false): DiceBounds | null {
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    let centerY = 0;
    let count = 0;

    for (const die of this.dice) {
      // Skip held dice if unheldOnly is true
      if (unheldOnly && die.held) continue;

      const pos = die.getPosition();
      minX = Math.min(minX, pos.x);
      maxX = Math.max(maxX, pos.x);
      minZ = Math.min(minZ, pos.z);
      maxZ = Math.max(maxZ, pos.z);
      centerY += pos.y;
      count++;
    }

    // No dice to track
    if (count === 0) return null;

    centerY /= count;

    // Reuse static vector instead of creating new one
    _boundsCenter.set(
      (minX + maxX) / 2,
      centerY,
      (minZ + maxZ) / 2
    );

    return {
      center: _boundsCenter,
      width: maxX - minX + this.config.diceSize,
      depth: maxZ - minZ + this.config.diceSize,
    };
  }

  /**
   * Calculate camera position to fit all dice in view
   * Uses reusable vectors to avoid per-frame allocations
   */
  private calculateCameraForBounds(
    bounds: DiceBounds,
    zoom: boolean
  ): { position: THREE.Vector3; lookAt: THREE.Vector3 } {
    const fov = this.camera.fov * (Math.PI / 180);
    const aspect = this.camera.aspect;

    // Calculate horizontal FOV from vertical FOV and aspect ratio
    const horizontalFov = 2 * Math.atan(Math.tan(fov / 2) * aspect);

    const paddingScale = zoom ? CameraAnims.zoomPaddingScale : 1;
    const widthWithPadding = bounds.width + this.config.padding * 2 * paddingScale;
    const depthWithPadding = bounds.depth + this.config.padding * 2 * paddingScale;

    // Distance needed to fit width (X axis)
    const distForWidth = (widthWithPadding / 2) / Math.tan(horizontalFov / 2);

    // Distance needed to fit depth (Z axis) - adjusted for camera angle
    // Camera looks down at an angle, so effective vertical FOV is reduced
    const effectiveVerticalFov = zoom ? fov : fov * CameraAnims.followDepthFovMultiplier;
    const distForDepth = (depthWithPadding / 2) / Math.tan(effectiveVerticalFov / 2);

    let distance = Math.max(distForWidth, distForDepth);

    // Reuse static vector for view direction
    _viewDir.set(0, 1, 0.6).normalize();
    const minDistanceForHeight = this.config.minHeight / _viewDir.y;

    // Apply minimum distance based on zoom mode
    if (zoom) {
      distance = Math.max(distance, minDistanceForHeight, CameraAnims.minZoomDistance);
    } else {
      distance = Math.max(distance, CameraAnims.minFollowDistance);
    }

    // Position camera above and behind the center of dice
    // Reuse static vector for position calculation
    _tempPosition.copy(bounds.center).addScaledVector(_viewDir, distance);

    return { position: _tempPosition, lookAt: bounds.center };
  }

  /**
   * Reset to default immediately (no lerp, no tween)
   */
  reset(): void {
    if (this.transitionTween) {
      this.transitionTween.kill();
      this.transitionTween = null;
    }
    this.mode = 'default';
    this.camera.position.copy(this.defaultPosition);
    this.currentLookAt.copy(this.defaultLookAt);
    this.camera.lookAt(this.currentLookAt);
  }
}
